// SaveJobDialog.jsx — save a run with a name, or rename a saved job
// (decided by Deepak with Mayank, 2 Nov, on #85).
//
// Saving asks for a name — there is no default: a job is kept because someone
// chose to keep it, under a name they chose. Once saved, the dialog says what
// happened to the cartons: drafts that went into the library, any of them that
// duplicate a library carton (saved anyway, and said so here — decided 2 Nov),
// and any that are gone. The words come from savedJobs.saveOutcome.

import React from 'react';
import { useTheme } from './theme';
import { NAME_MAX, nameProblem } from './savedJobs';

/**
 * @param {Object|null} dialog  { mode: 'save'|'rename', name } or null when hidden
 * @param {boolean} busy        a save or rename is in flight
 * @param {string|null} error   what the server refused, in plain words
 * @param {Object|null} outcome { title, lines } once saved
 * @param {(name: string) => void} onSubmit
 * @param {() => void} onClose
 */
export default function SaveJobDialog({ dialog, busy = false, error = null, outcome = null, onSubmit, onClose }) {
  const { P, FN, MO, DISPLAY } = useTheme();
  const [name, setName] = React.useState('');
  const [touched, setTouched] = React.useState(false);
  const inputRef = React.useRef(null);
  const doneRef = React.useRef(null);
  const open = !!dialog;

  React.useEffect(() => {
    if (!open) return;
    setName(dialog.name || '');
    setTouched(false);
  }, [open, dialog]);
  React.useEffect(() => {
    if (!open) return;
    if (outcome && doneRef.current) doneRef.current.focus();
    else if (inputRef.current) inputRef.current.focus();
  }, [open, outcome]);
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onClose]);

  if (!dialog) return null;
  const renaming = dialog.mode === 'rename';
  const problem = nameProblem(name);
  const submit = (e) => {
    e.preventDefault();
    setTouched(true);
    if (problem || busy) return;
    onSubmit(name.trim());
  };
  const btn = {
    fontFamily: MO, fontSize: 11.5, padding: '8px 16px', borderRadius: 6,
    cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.5 : 1,
  };

  return (
    <div onClick={busy ? undefined : onClose} style={{
      position: 'fixed', inset: 0, zIndex: 210, background: 'rgba(20,16,12,.45)',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '10vh 16px',
    }}>
      <div role="dialog" aria-modal="true" aria-label={renaming ? 'Rename saved job' : 'Save job'}
        onClick={(e) => e.stopPropagation()} style={{
          width: '100%', maxWidth: 480, background: P.sheet, border: `0.5px solid ${P.rule}`,
          borderRadius: 14, overflow: 'hidden', boxShadow: '0 24px 60px rgba(20,16,12,.28)',
        }}>
        <div style={{ padding: '16px 18px 10px' }}>
          <div style={{ fontFamily: MO, fontSize: 9.5, letterSpacing: 1.5, textTransform: 'uppercase', color: P.dim }}>
            {renaming ? 'Rename saved job' : 'Save job'}
          </div>
          {outcome ? (
            <>
              <div style={{ fontFamily: DISPLAY, fontSize: 20, color: P.ink, lineHeight: 1.2, marginTop: 4 }}>
                {outcome.title}
              </div>
              {outcome.lines.map((line) => (
                <div key={line} style={{ fontFamily: FN, fontSize: 12, color: P.ink, lineHeight: 1.5, marginTop: 8 }}>
                  {line}
                </div>
              ))}
              <div style={{ fontFamily: FN, fontSize: 11, color: P.dim, lineHeight: 1.5, marginTop: 10 }}>
                Open it again from the Jobs tray, under Saved jobs.
              </div>
            </>
          ) : (
            <div style={{ fontFamily: FN, fontSize: 12, color: P.muted, lineHeight: 1.5, marginTop: 6 }}>
              {renaming
                ? 'The new name is what the Jobs tray shows.'
                : 'Keeps this layout and its setup under a name, in the Jobs tray. Any draft carton it uses is saved to your library.'}
            </div>
          )}
        </div>

        {outcome ? (
          <div style={{ padding: '6px 18px 16px' }}>
            <button ref={doneRef} onClick={onClose} style={{
              ...btn, background: P.ink, color: P.sheet, border: `0.5px solid ${P.ink}`,
            }}>Done</button>
          </div>
        ) : (
          <form onSubmit={submit} style={{ padding: '6px 18px 16px' }}>
            <input ref={inputRef} value={name} maxLength={NAME_MAX + 50} disabled={busy}
              onChange={(e) => setName(e.target.value)} onBlur={() => setTouched(true)}
              placeholder="e.g. Diwali order — 5,000 cartons" aria-label="Job name"
              style={{
                width: '100%', boxSizing: 'border-box', fontFamily: FN, fontSize: 13, color: P.ink,
                padding: '9px 11px', borderRadius: 7, background: P.elev,
                border: `0.5px solid ${touched && problem ? P.dangerLine : P.rule}`, outline: 'none',
              }} />
            {touched && problem && (
              <div style={{ fontFamily: FN, fontSize: 11, color: P.danger, marginTop: 6 }}>{problem}</div>
            )}
            {error && (
              <div style={{ fontFamily: FN, fontSize: 11.5, color: P.danger, marginTop: 8, lineHeight: 1.45, whiteSpace: 'pre-line' }}>
                {error}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 14 }}>
              <button type="submit" disabled={busy} style={{
                ...btn, background: P.ink, color: P.sheet, border: `0.5px solid ${P.ink}`,
              }}>{busy ? (renaming ? 'Renaming…' : 'Saving…') : (renaming ? 'Rename' : 'Save job')}</button>
              <button type="button" onClick={onClose} disabled={busy} style={{
                ...btn, background: 'transparent', color: P.muted, border: `0.5px solid ${P.rule}`,
              }}>Cancel</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
